<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="variations-modal" @click.stop>

      <!-- Header -->
      <div class="modal-header">
        <div class="modal-header-left">
          <div class="modal-title">
            <span class="modal-title-icon">🎨</span>
            <span>Article Variations</span>
            <span class="modal-site-url">— {{ siteUrl }}</span>
          </div>
          <div class="modal-subtitle">Define how articles are generated for each touchpoint audience</div>
        </div>
        <button class="modal-close-btn" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">

        <!-- Info box -->
        <div class="info-box">
          <i class="fas fa-info-circle" style="flex-shrink:0;margin-top:1px;"></i>
          <span>Each <strong>variation profile</strong> controls which prompt styles are used when generating articles. You can create multiple profiles and assign one to this source.</span>
        </div>

        <!-- Variation selector row -->
        <div class="var-selector-row">
          <label class="var-selector-label">Variation:</label>

          <!-- Existing mode: dropdown -->
          <select
            v-show="!isNewMode"
            v-model="selectedProfileID"
            class="var-select"
            @change="onSelectProfile">
            <option v-if="!atVariationLimit" value="__new__">+ Create new variation…</option>
            <option v-if="!savedProfiles.length" :value="null" disabled>No profiles yet</option>
            <option v-for="p in savedProfiles" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select>

          <!-- New mode: name text input -->
          <input
            v-show="isNewMode"
            v-model="variationName"
            ref="nameInputRef"
            type="text"
            class="var-name-input"
            placeholder="Enter variation name…"
            @input="isDirty = true" />

          <!-- Existing / New toggle -->
          <div class="var-mode-toggle">
            <button
              class="var-mode-btn"
              :class="{ active: !isNewMode }"
              :disabled="!savedProfiles.length"
              @click="switchMode('existing')">
              Existing
            </button>
            <button
              class="var-mode-btn"
              :class="{ active: isNewMode }"
              :disabled="atVariationLimit"
              :title="atVariationLimit ? `Maximum ${MAX_VARIATIONS} profiles reached` : ''"
              @click="switchMode('new')">
              + New
            </button>
          </div>

          <!-- Delete button (existing mode only) -->
          <button
            v-show="!isNewMode && selectedProfileID"
            class="btn-delete-var"
            @click="deleteVariation"
            title="Delete this variation">
            <i class="fas fa-trash"></i>
          </button>
        </div>

        <!-- Priority selector row (show for both existing and new) -->
        <div class="priority-selector-row" v-show="selectedProfileID || isNewMode">
            <label class="var-priority-label">Priority:</label>

            <select
              v-model.number="selectedPriority"
              class="priority-select"
              :disabled="isNewMode"
              @change="isNewMode ? isDirty = true : updateProfilePriority()">
              <option
                v-for="num in availablePriorities"
                :key="num"
                :value="num">
                Priority {{ num }}{{ getProfileWithPriority(num) ? ` (Currently ${getProfileWithPriority(num)})` : '' }}
              </option>
            </select>

            <span v-if="isNewMode" class="priority-hint">Auto-assigned to {{ selectedPriority }} (editable after save)</span>
        </div>

        <!-- Loading state -->
        <div v-show="loadingProfiles" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i> Loading profiles…
        </div>

        <!-- Touchpoint group cards -->
        <div v-show="!loadingProfiles">
          <div class="section-label" style="margin-bottom:10px;">
            <i class="fas fa-bullseye"></i>
            Touchpoint Groups — select which to include
          </div>

          <div
            v-for="variation in variations"
            :key="variation.id"
            class="tp-group"
            :class="{ 'tp-group-checked': variation.enabled }"
            @click.stop="toggleVariation(variation.id)"
            style="cursor:pointer;">
            <div class="tp-group-header">
              <span class="tp-group-check-wrap">
                <span class="tp-group-check-box" :class="{ 'is-unchecked': !variation.enabled }">
                  <i v-show="variation.enabled" class="fas fa-check" style="font-size:10px;"></i>
                </span>
              </span>
              <div class="tp-group-info">
                <div class="tp-group-name">
                  <i :class="variation.icon" class="tp-group-icon"></i>
                  {{ variation.name }}
                  <span class="tp-prompt-label">prompt style</span>
                </div>
                <div class="tp-group-desc">{{ variation.description }}</div>
                <div class="tp-chips">
                  <span
                    v-for="tag in variation.touchpoints"
                    :key="tag.label"
                    class="tp-chip"
                    :class="tag.color">
                    {{ tag.label }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Existing variations list -->
        <div v-show="savedProfiles.length > 0 && !loadingProfiles" class="existing-vars-section">
          <div class="section-divider"></div>
          <div class="section-label">
            <i class="fas fa-layer-group"></i>
            Saved Profiles for this Source
          </div>
          <div class="var-list">
            <div
              v-for="p in savedProfiles"
              :key="p.id"
              class="var-item"
              :class="{ 'var-item-selected': selectedProfileID === p.id && !isNewMode }"
              @click="selectExistingProfile(p)">
              <div>
                <div class="var-item-name">{{ p.name }}</div>
                <div class="var-item-meta">{{ p.prompt_styles.length }} style{{ p.prompt_styles.length !== 1 ? 's' : '' }}</div>
              </div>
              <div class="var-item-chips">
                <span
                  v-for="sid in p.prompt_styles"
                  :key="sid"
                  class="var-item-chip"
                  :class="getStyleChipColor(sid)">
                  {{ getStyleLabel(sid) }}
                </span>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- Error banner -->
      <div v-if="saveError" class="save-error">{{ saveError }}</div>

      <!-- Footer -->
      <div class="modal-footer">
        <button class="btn-cancel" @click="$emit('close')">Cancel</button>
        <button class="btn-save" @click="save" :disabled="saving">
          <i v-show="saving" class="fas fa-spinner fa-spin" style="margin-right:6px;"></i>
          <i v-show="!saving" class="fas fa-save" style="margin-right:6px;"></i>
          {{ saving ? 'Saving…' : 'Save Profile' }}
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, computed, watch } from 'vue'

const props = defineProps({
  siteUrl:  { type: String,  default: '' },
  siteID:   { type: Number,  default: null },
  siteType: { type: String,  default: 'site' },  // 'site' | 'directory'
  pageID:   { type: Number,  default: null },     // set when editing a page-level override
  fileID:   { type: Number,  default: null },     // set when editing a file-level override
})

const emit = defineEmits(['close', 'save'])

// ── State ─────────────────────────────────────────────────────────────────────
const isNewMode        = ref(false)
const selectedProfileID = ref(null)
const variationName    = ref('')
const nameInputRef     = ref(null)
const loadingProfiles  = ref(false)
const saving           = ref(false)
const saveError        = ref('')
const savedProfiles    = ref([])
const activeProfileID  = ref(null)  // kept for save() compat
const inheritedMode    = ref(false) // true when profiles were loaded from parent (site/dir), not page/file
const isDirty          = ref(false) // true when user has made a change since modal opened
const pendingChanges   = ref({})    // { [profileID]: { prompt_styles: [], priority: null } } — unsaved edits per profile
const selectedPriority = ref(null)  // priority for current profile (1, 2, 3... n)

// ── Canonical style definitions ───────────────────────────────────────────────
const STYLE_DEFS = [
  {
    id: 'voice_chat',
    name: 'Voice & Chat',
    icon: 'fas fa-microphone',
    description: 'Concise articles optimised for voice interactions and chat bot responses.',
    touchpoints: [
      { label: 'Digital Bot Flow', color: 'tp-chip-purple' },
      { label: 'Bot Flow',         color: 'tp-chip-purple' },
    ]
  },
  {
    id: 'agent_only',
    name: 'Agent Only',
    icon: 'fas fa-headset',
    description: 'Detailed articles with full context, helping agents provide accurate support to members.',
    touchpoints: [
      { label: 'Agent CoPilot', color: 'tp-chip-blue' },
    ]
  },
  {
    id: 'knowledge_portal',
    name: 'Knowledge Portal',
    icon: 'fas fa-globe',
    description: 'Rich, public-facing articles and full formatting for the web knowledge portal.',
    touchpoints: [
      { label: 'Knowledge App',    color: 'tp-chip-teal' },
      { label: 'Knowledge Portal', color: 'tp-chip-teal' },
    ]
  }
]

const variations = ref(STYLE_DEFS.map(s => ({ ...s, enabled: true })))

// ── Style label / chip helpers ────────────────────────────────────────────────
const STYLE_LABELS      = { voice_chat: 'Voice & Chat', agent_only: 'Agent Only', knowledge_portal: 'Knowledge Portal' }
const STYLE_CHIP_COLORS = { voice_chat: 'tp-chip-purple', agent_only: 'tp-chip-blue', knowledge_portal: 'tp-chip-teal' }
const getStyleLabel     = (id) => STYLE_LABELS[id] || id
const getStyleChipColor = (id) => STYLE_CHIP_COLORS[id] || ''

// ── Backend ───────────────────────────────────────────────────────────────────
const apiBase = process.env.VUE_APP_AI_BASE_URL

async function fetchProfiles() {
  loadingProfiles.value = true
  try {
    // For page/file overrides: try custom profiles first, fall back to parent profiles #20260415KL
    if (props.pageID) {
      const res = await fetch(`${apiBase}/api/variation-profiles?page_id=${props.pageID}`)
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`)
      }
      const pageProfiles = await res.json()
      if (pageProfiles.length) {
        savedProfiles.value = pageProfiles
        inheritedMode.value = false
        return
      }
      // No custom profiles — show inherited parent profiles as starting point #20260428KL
      if (props.siteID) {
        const fallbackParam = props.siteType === 'directory' ? `directory_id=${props.siteID}` : `site_id=${props.siteID}`
        const fallback = await fetch(`${apiBase}/api/variation-profiles?${fallbackParam}`)
        if (fallback.ok) {
          savedProfiles.value = await fallback.json()
        }
      }
      inheritedMode.value = true
      return
    }
    if (props.fileID) {
      const res = await fetch(`${apiBase}/api/variation-profiles?file_id=${props.fileID}`)
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`)
      }
      const fileProfiles = await res.json()
      if (fileProfiles.length) {
        savedProfiles.value = fileProfiles
        inheritedMode.value = false
        return
      }
      if (props.siteID) {
        const param = props.siteType === 'directory' ? `directory_id=${props.siteID}` : `site_id=${props.siteID}`
        const fallback = await fetch(`${apiBase}/api/variation-profiles?${param}`)
        if (fallback.ok) {
          savedProfiles.value = await fallback.json()
        }
      }
      inheritedMode.value = true
      return
    }
    inheritedMode.value = false
    // Site or directory level #20260415KL
    const param = props.siteType === 'directory' ? `directory_id=${props.siteID}` : `site_id=${props.siteID}`
    const res = await fetch(`${apiBase}/api/variation-profiles?${param}`)
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`)
    }
    savedProfiles.value = await res.json()
  } catch (e) {
    console.error('Failed to load variation profiles:', e)
  } finally {
    loadingProfiles.value = false
  }
}

// Auto-assign priorities to profiles that have NULL priority #20260420KL
async function assignMissingPriorities() {
  const profilesWithoutPriority = savedProfiles.value.filter(p => p.priority === null)
  if (!profilesWithoutPriority.length) {
    return
  }

  for (let i = 0; i < profilesWithoutPriority.length; i++) {
    const profile = profilesWithoutPriority[i]
    const newPriority = i + 1
    try {
      await fetch(`${apiBase}/api/variation-profiles/${profile.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: profile.name,
          prompt_styles: profile.prompt_styles,
          priority: newPriority
        })
      })
      profile.priority = newPriority
    } catch (e) {
      console.error(`Failed to assign priority to profile ${profile.id}:`, e)
    }
  }
}

onMounted(async () => {
  await fetchProfiles()
  await assignMissingPriorities()
  if (savedProfiles.value.length) {
    selectedProfileID.value = savedProfiles.value[0].id
    isNewMode.value = false
    onSelectProfile()
  } else {
    isNewMode.value = true
  }
})

// ── Recalculate auto-assigned priority when profiles change (deletion) ──────────
watch([savedProfiles, isNewMode], () => {
  if (isNewMode.value) {
    selectedPriority.value = savedProfiles.value.length + 1
  }
})

// ── Mode switching ────────────────────────────────────────────────────────────
function switchMode(mode) {
  if (mode === 'existing') {
    if (!savedProfiles.value.length) {
      return
    }
    isNewMode.value = false
    // Reset to first real profile if nothing valid is selected #20260415KL
    if (!selectedProfileID.value || selectedProfileID.value === '__new__') {
      selectedProfileID.value = savedProfiles.value[0].id
    }
    onSelectProfile()
  } else {
    if (atVariationLimit.value) {
      saveError.value = `Maximum of ${MAX_VARIATIONS} variation profiles allowed. Delete one before adding another.`
      return
    }
    // Snapshot current profile's unsaved edits before leaving it #20260429KL
    if (activeProfileID.value) {
      const profileIDStr = String(activeProfileID.value)
      pendingChanges.value[profileIDStr] = {
        prompt_styles: variations.value.filter(v => v.enabled).map(v => v.id),
        priority: selectedPriority.value
      }
    }
    isNewMode.value = true
    isDirty.value = true  // switching to new = intentional action
    variationName.value = ''
    activeProfileID.value = null
    selectedPriority.value = savedProfiles.value.length + 1  // Auto-assign next priority
    variations.value.forEach(v => { v.enabled = false })
    nextTick(() => nameInputRef.value?.focus())
  }
}

function onSelectProfile() {
  if (selectedProfileID.value === '__new__') {
    switchMode('new')
    return
  }
  // Snapshot current variation state before switching away #20260416KL
  if (activeProfileID.value) {
    const profileIDStr = String(activeProfileID.value)
    pendingChanges.value[profileIDStr] = {
      prompt_styles: variations.value.filter(v => v.enabled).map(v => v.id),
      priority: selectedPriority.value
    }
  }
  const selectedProfile = savedProfiles.value.find(profile => profile.id === selectedProfileID.value)
  if (selectedProfile) {
    variationName.value = selectedProfile.name
    activeProfileID.value = selectedProfile.id
    // Restore any unsaved edits for this profile, else load from saved #20260416KL
    const profileIDStr = String(selectedProfile.id)
    const pending = pendingChanges.value[profileIDStr]
    if (pending) {
      selectedPriority.value = pending.priority !== undefined ? pending.priority : selectedProfile.priority || null
      variations.value.forEach(v => { v.enabled = pending.prompt_styles.includes(v.id) })
    } else {
      selectedPriority.value = selectedProfile.priority || null
      variations.value.forEach(v => { v.enabled = selectedProfile.prompt_styles.includes(v.id) })
    }
  }
}

function selectExistingProfile(profile) {
  selectedProfileID.value = profile.id
  isNewMode.value = false
  onSelectProfile()
}

// ── Card toggle ───────────────────────────────────────────────────────────────
function toggleVariation(id) {
  const item = variations.value.find(v => v.id === id)
  if (item) {
    item.enabled = !item.enabled
    isDirty.value = true
  }
}

// ── Delete ────────────────────────────────────────────────────────────────────
async function deleteVariation() {
  if (!selectedProfileID.value) {
    return
  }
  const profileToDelete = savedProfiles.value.find(profile => profile.id === selectedProfileID.value)
  if (!profileToDelete) {
    return
  }
  if (!confirm(`Delete profile "${profileToDelete.name}"? This cannot be undone.`)) {
    return
  }

  try {
    if (inheritedMode.value) {
      // We're viewing inherited (parent-level) profiles — do NOT delete the parent profile.
      // Instead, copy every profile except the deleted one down to the page/file level
      // so the remaining ones become a custom override without touching the parent. #20260428KL
      let sourceScope
      if (props.pageID) {
        sourceScope = { page_id: props.pageID }
      } else if (props.fileID) {
        sourceScope = { file_id: props.fileID }
      } else if (props.siteType === 'directory') {
        sourceScope = { directory_id: props.siteID }
      } else {
        sourceScope = { site_id: props.siteID }
      }

      const profilesToKeep = savedProfiles.value.filter(x => x.id !== selectedProfileID.value)
      const created = []
      for (let i = 0; i < profilesToKeep.length; i++) {
        const p = profilesToKeep[i]
        const res = await fetch(`${apiBase}/api/variation-profiles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: p.name, prompt_styles: p.prompt_styles, priority: i + 1, ...sourceScope }),
        })
        if (res.ok) {
          created.push(await res.json())
        }
      }
      savedProfiles.value = created
      inheritedMode.value = false
      // Notify parent that this page/file now has custom profiles, but keep modal open #20260428KL
      if (props.pageID || props.fileID) {
        emit('save', { hasCustomVariations: true, keepOpen: true })
      }
    } else {
      const res = await fetch(`${apiBase}/api/variation-profiles/${selectedProfileID.value}`, {
        method: 'DELETE',
      })
      if (!res.ok && res.status !== 204) {
        throw new Error(`HTTP ${res.status}`)
      }
      savedProfiles.value = savedProfiles.value.filter(x => x.id !== selectedProfileID.value)
    }

    // Resequence priorities so they stay contiguous after deletion.
    // Clear pendingChanges first — any stale priority swaps for the deleted or
    // reordered profiles would otherwise replay on the next Save and corrupt priorities. #20260428KL
    pendingChanges.value = {}

    for (let i = 0; i < savedProfiles.value.length; i++) {
      const profile = savedProfiles.value[i]
      const newPriority = i + 1
      if (profile.priority !== newPriority) {
        profile.priority = newPriority
        fetch(`${apiBase}/api/variation-profiles/${profile.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: profile.name, prompt_styles: profile.prompt_styles, priority: newPriority })
        }).catch(() => {})
      }
    }

    if (savedProfiles.value.length) {
      selectedProfileID.value = savedProfiles.value[0].id
      isNewMode.value = false
      onSelectProfile()
    } else {
      selectedProfileID.value = null
      switchMode('new')
      // All custom profiles removed — notify parent so the badge reverts to Inherited #20260428KL
      if (props.pageID || props.fileID) {
        emit('save', { hasCustomVariations: false })
      }
    }
  } catch (e) {
    saveError.value = `Delete failed: ${e.message}`
  }
}

// ── Priority management ───────────────────────────────────────────────────────
const availablePriorities = computed(() => {
  // Show all priorities from 1 to n+1 when in new mode (creating new profile)
  // or 1 to n when in existing mode #20260420KL
  const maxPriority = isNewMode.value ? savedProfiles.value.length + 1 : savedProfiles.value.length
  return Array.from({ length: maxPriority }, (_, i) => i + 1)
})

const getProfileWithPriority = (priority) => {
  // Find which profile currently has this priority #20260420KL
  return savedProfiles.value.find(p => p.priority === priority)?.name || null
}

function updateProfilePriority() {
  if (selectedPriority.value === null || selectedPriority.value === '') {
    isDirty.value = true
    return
  }

  const currentProfile = savedProfiles.value.find(p => p.id === selectedProfileID.value)
  const profileWithSelectedPriority = savedProfiles.value.find(
    p => p.id !== selectedProfileID.value && p.priority === selectedPriority.value
  )

  const currentIDStr = String(selectedProfileID.value)
  const otherIDStr = profileWithSelectedPriority ? String(profileWithSelectedPriority.id) : null

  if (profileWithSelectedPriority && currentProfile) {
    // SWAP: Give other profile the current one's old priority #20260420KL
    const oldPriority = currentProfile.priority

    // Update both in memory #20260420KL
    currentProfile.priority = selectedPriority.value
    profileWithSelectedPriority.priority = oldPriority

    // Track BOTH changes in pending changes #20260420KL
    // Current profile gets new priority #20260420KL
    if (!pendingChanges.value[currentIDStr]) {
      pendingChanges.value[currentIDStr] = {
        prompt_styles: currentProfile.prompt_styles || [],
        priority: selectedPriority.value
      }
    } else {
      pendingChanges.value[currentIDStr].priority = selectedPriority.value
    }

    // Other profile gets old priority #20260420KL
    if (otherIDStr) {
      if (!pendingChanges.value[otherIDStr]) {
        pendingChanges.value[otherIDStr] = {
          prompt_styles: profileWithSelectedPriority.prompt_styles || [],
          priority: oldPriority
        }
      } else {
        pendingChanges.value[otherIDStr].priority = oldPriority
      }
    }
  }

  isDirty.value = true
}

// ── Variation limit ───────────────────────────────────────────────────
const MAX_VARIATIONS = 10
const atVariationLimit = computed(() => savedProfiles.value.length >= MAX_VARIATIONS)

// ── Validation ───────────────────────────────────────────────────────────────
function nameAlreadyExists(name, excludeProfileID = null) {
  return savedProfiles.value.some(p =>
    p.name.toLowerCase() === name.toLowerCase() && p.id !== excludeProfileID
  )
}

// ── Save ──────────────────────────────────────────────────────────────────────
async function save() {
  saveError.value = ''
// If viewing inherited profiles with no changes, just close — don't create an override #20260416KL
  if (inheritedMode.value && !isDirty.value) {
    emit('close')
    return
  }

  const name = isNewMode.value
    ? variationName.value.trim()
    : (savedProfiles.value.find(profile => profile.id === selectedProfileID.value)?.name || '')

  if (!name) {
    saveError.value = 'Please enter a profile name.'
    return
  }

  // Check for duplicate names at this scope level #20260420KL
  const excludeID = isNewMode.value ? null : selectedProfileID.value
  if (nameAlreadyExists(name, excludeID)) {
    saveError.value = `A variation with name "${name}" already exists at this level.`
    return
  }

  const enabledStyles = variations.value.filter(v => v.enabled).map(v => v.id)
  if (!enabledStyles.length) {
    saveError.value = 'Please select at least one prompt style.'
    return
  }

  saving.value = true
  try {
    let sourceScope
    if (props.pageID) {
      sourceScope = { page_id: props.pageID }
    } else if (props.fileID) {
      sourceScope = { file_id: props.fileID }
    } else if (props.siteType === 'directory') {
      sourceScope = { directory_id: props.siteID }
    } else {
      sourceScope = { site_id: props.siteID }
    }

    // Flush current variations into pendingChanges before saving #20260416KL
    if (activeProfileID.value && !isNewMode.value) {
      pendingChanges.value[String(activeProfileID.value)] = {
        prompt_styles: enabledStyles,
        priority: selectedPriority.value
      }
    }

    if (inheritedMode.value) {
      // Copy ALL inherited profiles to page/file level. #20260415KL
      // Apply any pending edits per profile; fall back to original prompt_styles. #20260416KL
      for (const profile of savedProfiles.value) {
        const pending = pendingChanges.value[profile.id]
        const styles = pending?.prompt_styles !== undefined ? pending.prompt_styles : profile.prompt_styles
        const pPriority = pending?.priority !== undefined ? pending.priority : profile.priority
        const pName = profile.id === selectedProfileID.value && !isNewMode.value ? name : profile.name
        await fetch(`${apiBase}/api/variation-profiles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: pName, prompt_styles: styles, priority: pPriority, ...sourceScope }),
        })
      }
      // If creating a brand-new variation (not editing an existing one), add it too #20260415KL
      if (isNewMode.value) {
        await fetch(`${apiBase}/api/variation-profiles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, prompt_styles: enabledStyles, priority: selectedPriority.value, ...sourceScope }),
        })
      }
      inheritedMode.value = false
    } else if (isNewMode.value) {
      if (atVariationLimit.value) {
        saveError.value = `Maximum of ${MAX_VARIATIONS} variation profiles allowed. Delete one before adding another.`
        return
      }
      // Flush pending changes for existing profiles before adding the new one #20260429KL
      for (const [profileID, changes] of Object.entries(pendingChanges.value)) {
        const profile = savedProfiles.value.find(p => String(p.id) === profileID)
        if (!profile) {
          continue
        }
        const styles = changes.prompt_styles !== undefined ? changes.prompt_styles : profile.prompt_styles
        const pPriority = changes.priority !== undefined ? changes.priority : profile.priority
        const res = await fetch(`${apiBase}/api/variation-profiles/${profileID}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: profile.name, prompt_styles: styles, priority: pPriority }),
        })
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        const updated = await res.json()
        const idx = savedProfiles.value.findIndex(p => p.id === profileID)
        if (idx >= 0) {
          savedProfiles.value[idx] = updated
        }
      }
      // Add a new profile to the existing page/file-level set #20260415KL
      const res = await fetch(`${apiBase}/api/variation-profiles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, prompt_styles: enabledStyles, priority: selectedPriority.value, ...sourceScope }),
      })
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`)
      }
      const created = await res.json()
      savedProfiles.value.push(created)
      selectedProfileID.value = created.id
      isNewMode.value = false
    } else {
      // Update all profiles that have pending changes #20260416KL
      for (const [profileID, changes] of Object.entries(pendingChanges.value)) {
        const profile = savedProfiles.value.find(savedProfile => String(savedProfile.id) === profileID)
        if (!profile) {
          continue
        }
        const pName = profileID === selectedProfileID.value ? name : profile.name
        const styles = changes.prompt_styles !== undefined ? changes.prompt_styles : profile.prompt_styles
        const pPriority = changes.priority !== undefined ? changes.priority : profile.priority
        const res = await fetch(`${apiBase}/api/variation-profiles/${profileID}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: pName, prompt_styles: styles, priority: pPriority }),
        })
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        const updated = await res.json()
        const idx = savedProfiles.value.findIndex(p => p.id === profileID)
        if (idx >= 0) {
          savedProfiles.value[idx] = updated
        }
      }
    }

    emit('save', { name, prompt_styles: enabledStyles })
    emit('close')
  } catch (e) {
    console.error('Save variation profile failed:', e)
    saveError.value = `Save failed: ${e.message}`
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.variations-modal {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  width: 600px;
  max-width: 95vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Header ──────────────────────────────────────────────── */
.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 22px 14px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.modal-title-icon { color: #6366f1; }

.modal-site-url {
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
}

.modal-subtitle {
  margin-top: 3px;
  font-size: 12px;
  color: #6b7280;
}

.modal-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  font-size: 16px;
  padding: 2px 4px;
  border-radius: 4px;
  transition: color 0.15s;
}
.modal-close-btn:hover { color: #374151; }

/* ── Body (scrollable) ───────────────────────────────────── */
.modal-body {
  padding: 18px 22px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* ── Info box ────────────────────────────────────────────── */
.info-box {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 10px 13px;
  font-size: 11px;
  color: #1e40af;
  line-height: 1.6;
}

/* ── Variation selector row ──────────────────────────────── */
.var-selector-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
}

.priority-selector-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
}

.var-selector-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  min-width: 72px;
  align-self: center;
}

.var-select {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #111827;
  background: #fff;
  outline: none;
  min-width: 0;
}
.var-select:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.15);
}

.var-name-input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #111827;
  outline: none;
  min-width: 0;
}
.var-name-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.15);
}

.var-priority-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  min-width: 72px;
  align-self: center;
}

.priority-select {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #111827;
  background: #fff;
  outline: none;
  min-width: 120px;
}
.priority-select:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.15);
}

.priority-hint {
  font-size: 12px;
  color: #6b7280;
  margin-left: 8px;
  font-style: italic;
  white-space: nowrap;
}

.var-mode-toggle {
  display: flex;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
}

.var-mode-btn {
  padding: 5px 11px;
  font-size: 11px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  background: #fff;
  color: #6b7280;
  transition: all 0.12s;
}
.var-mode-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.var-mode-btn.active {
  background: #8b5cf6;
  color: #fff;
}

.btn-delete-var {
  padding: 5px 9px;
  border: 1px solid #fecaca;
  border-radius: 6px;
  background: #fff;
  color: #ef4444;
  font-size: 12px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
}
.btn-delete-var:hover {
  background: #ef4444;
  color: #fff;
}

/* ── Section label ───────────────────────────────────────── */
.section-label {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.section-divider {
  height: 1px;
  background: #f3f4f6;
}

/* ── Touchpoint group cards ──────────────────────────────── */
.tp-group {
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  margin-bottom: 10px;
  overflow: hidden;
  transition: border-color 0.15s;
}
.tp-group-checked {
  border-color: #8b5cf6;
}

.tp-group-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 13px 15px;
  background: #fff;
  transition: background 0.12s;
}
.tp-group:hover .tp-group-header {
  background: #faf5ff;
}

.tp-group-check-wrap {
  flex-shrink: 0;
  margin-top: 2px;
}

.tp-group-check-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid #8b5cf6;
  background: #8b5cf6;
  color: #fff;
  transition: all 0.15s;
}
.tp-group-check-box.is-unchecked {
  background: #fff;
  border-color: #d1d5db;
}

.tp-group-info { flex: 1; }

.tp-group-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 3px;
}

.tp-group-icon {
  font-size: 12px;
  color: #6b7280;
}

.tp-prompt-label {
  font-size: 10px;
  font-weight: 400;
  color: #9ca3af;
}

.tp-group-desc {
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 7px;
  line-height: 1.5;
}

.tp-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.tp-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
}
.tp-chip-purple { background: #ede9fe; color: #6d28d9; }
.tp-chip-blue   { background: #dbeafe; color: #1d4ed8; }
.tp-chip-teal   { background: #ccfbf1; color: #065f46; }

/* ── Existing variations list ────────────────────────────── */
.existing-vars-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.var-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.var-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 13px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: all 0.12s;
}
.var-item:hover {
  border-color: #8b5cf6;
  background: #faf5ff;
}
.var-item-selected {
  border-color: #8b5cf6;
  background: #ede9fe;
}

.var-item-name {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}
.var-item-meta {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 1px;
}

.var-item-chips {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.var-item-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
}

/* ── Loading ─────────────────────────────────────────────── */
.loading-state {
  padding: 20px;
  text-align: center;
  font-size: 13px;
  color: #6b7280;
}

/* ── Error ───────────────────────────────────────────────── */
.save-error {
  margin: 0 22px 8px;
  padding: 8px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  font-size: 12px;
  color: #dc2626;
  flex-shrink: 0;
}

/* ── Footer ──────────────────────────────────────────────── */
.modal-footer {
  padding: 13px 22px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  background: #fafafa;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 8px 16px;
  border-radius: 7px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-cancel:hover { background: #f9fafb; }

.btn-save {
  padding: 8px 16px;
  border-radius: 7px;
  border: none;
  background: #8b5cf6;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.15s;
}
.btn-save:hover:not(:disabled) { background: #7c3aed; }
.btn-save:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
